import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, CheckCircle, X } from "@phosphor-icons/react";

/**
 * Touch / stylus / mouse-friendly signature pad.
 * Renders a canvas the recipient can sign on. Calls `onSave(dataUrl)` with a
 * PNG data URL when the user confirms, or `onCancel()` if they back out.
 *
 * Pure CSS + canvas — no third-party library.
 */
export default function SignaturePad({ onSave, onCancel, height = 220, hint }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasStrokes, setHasStrokes] = useState(false);
  // Mandatory: name of the person who signed for the delivery (PRD)
  const [signedByName, setSignedByName] = useState("");

  // Size the canvas to its display width with HiDPI support
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = Math.floor(rect.width * ratio);
    c.height = Math.floor(rect.height * ratio);
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#18181b";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const pointerPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    if (e.touches && e.touches.length) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pointerPos(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasStrokes) setHasStrokes(true);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasStrokes(false);
  };

  const confirm = () => {
    if (!hasStrokes) return;
    const name = signedByName.trim();
    if (!name) return; // button is disabled in this case anyway
    const c = canvasRef.current;
    onSave(c.toDataURL("image/png"), name);
  };

  return (
    <div className="w-full" data-testid="signature-pad">
      <div className="text-[11px] text-zinc-500 mb-1.5">{hint || "Recipient: sign below with your finger or stylus"}</div>
      <div
        className="bg-white border-2 border-dashed border-zinc-300 rounded-md overflow-hidden touch-none"
        style={{ height }}
      >
        <canvas
          ref={canvasRef}
          className="w-full h-full block touch-none"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="signature-canvas"
        />
      </div>
      {/* PRD: mandatory text field for the name of the person who signed */}
      <div className="mt-3">
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-1">
          Name of person who signed <span className="text-red-600">*</span>
        </label>
        <input
          type="text"
          value={signedByName}
          onChange={(e) => setSignedByName(e.target.value)}
          placeholder="e.g. Jane Smith"
          required
          maxLength={120}
          className="w-full rounded-md border border-zinc-300 focus:border-[#002BF6] focus:ring-1 focus:ring-[#002BF6] outline-none px-3 py-2 text-sm"
          data-testid="signed-by-name"
        />
        <p className="text-[10px] text-zinc-500 mt-1">Required. This name is logged for proof-of-delivery.</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button type="button" variant="outline" size="sm" onClick={clear} data-testid="signature-clear">
          <ArrowsClockwise size={13} className="mr-1.5" /> Clear
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid="signature-cancel">
            <X size={13} className="mr-1.5" /> Cancel
          </Button>
        )}
        <Button
          type="button"
          onClick={confirm}
          disabled={!hasStrokes || !signedByName.trim()}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          size="sm"
          data-testid="signature-save"
          title={!hasStrokes ? "Draw signature first" : !signedByName.trim() ? "Enter signer's name" : ""}
        >
          <CheckCircle size={13} weight="bold" className="mr-1.5" /> Save signature
        </Button>
      </div>
    </div>
  );
}
